import env from '@fipnooone/react-env';
import axios, { AxiosError } from 'axios';
import Echo, { Broadcaster } from 'laravel-echo';
import Pusher from 'pusher-js';

import { ConsoleDev } from '@/Lib/ConsoleDev';
import { StationEvents } from '@/Services/IRomaStationWS/StationEvents';

import { Chamber } from './Chamber';

type _Echo = Echo<'reverb'>;

interface IRomaStationWsCreateOptions {
    echo: _Echo;
    userAccessToken: string;
}

type PresenceChannel = Broadcaster['reverb']['presence'];

export class IRomaStationWS {
    private readonly _echo: _Echo;

    private readonly _channelAuthAuthorizerApiUrl = `${env('BACKEND_URL')}/v1/broadcasting/auth`;

    private _joinedChannel: PresenceChannel | null = null;

    constructor(options: IRomaStationWsCreateOptions) {
        this._echo = options.echo;

        this._configureChannelAuthorizer(options.userAccessToken);
    }

    public static async createInstance(userAccessToken: string): Promise<IRomaStationWS> {
        const echo = new Echo<'reverb'>({
            Pusher,
            broadcaster: 'reverb',
            key: env('REVERB_KEY'),
            wsHost: env('WS_HOST'),
            wsPort: Number(env('WS_PORT')),
            wssPort: Number(env('WSS_PORT')),
            forceTLS: env('FORCE_TLS') === '1',
        });

        const connectedPromise = new Promise((resolve, reject) => {
            const updateSocketId = (data: Record<string, string>) => {
                if (data) {
                    resolve(data);
                } else {
                    reject(new Error('socket is null'));
                }

                echo.connector.pusher.connection.unbind('connected', updateSocketId);
            };

            echo.connector.pusher.connection.bind('connected', updateSocketId);
        });

        await connectedPromise;

        return new IRomaStationWS({
            echo,
            userAccessToken,
        });
    }

    public async destroyInstance() {
        if (this._joinedChannel) {
            this._joinedChannel.stopListeningToAll();
            this._joinedChannel = null;
        }

        this._echo.disconnect();
    }

    public async joinToStation(stationId: number): Promise<void> {
        /**
         * Promisify for handle 403 error when join to presence channel and try to authorize user.
         */
        const joinPromise = new Promise<void>((resolve, reject) => {
            this._joinedChannel = this._echo.join(`${StationEvents.maintenanceStationChannel(stationId)}`);

            /**
             * TODO: how unsubscribe from here event?
             */
            this._joinedChannel.here(resolve);

            /**
             * TODO: how unsubscribe from error event?
             */
            this._joinedChannel.error(reject);

            this._joinedChannel.listenToAll(ConsoleDev.log.bind(null, '[IRomaStationWS]: All events: '));
        });

        return joinPromise;
    }

    /**
     * Send chambers:edit
     * Wait returned chambers:state
     */
    public async sendChambersStartEdit(stationId: number, deviceId: number): Promise<Chamber[]> {
        const payload = { device_id: deviceId };

        const promise = new Promise<Chamber[]>((resolve) => {
            const onChambersState = (data: { chambers: Chamber[]; device_id: number }) => {
                this._joinedChannel?.stopListening(`.${StationEvents.chambersState()}`, onChambersState);

                return resolve(data.chambers);
            };

            this._joinedChannel?.listen(`.${StationEvents.chambersState()}`, onChambersState);

            this._echo.connector.pusher.send_event(
                StationEvents.chambersStartEdit(),
                payload,
                `presence-${StationEvents.maintenanceStationChannel(stationId)}`
            );
        });

        return promise;
    }

    /**
     * Send chambers:save
     *
     * @return deviceId
     */
    public async sendChambersSave(stationId: number, deviceId: number, chambers: Chamber[]): Promise<number> {
        if (!this._joinedChannel) {
            throw new Error('First connect to channel.');
        }

        const payload = { device_id: deviceId, chambers };

        const promise = new Promise<number>((resolve, reject) => {
            const onChambersSaveAck = (data: { device_id: number }) => {
                this._joinedChannel?.stopListening(`.${StationEvents.chambersSaveAck()}`, onChambersSaveAck);

                return resolve(data.device_id);
            };

            const onChambersSaveNack = (data: { device_id: number }) => {
                this._joinedChannel?.stopListening(`.${StationEvents.chambersSaveNack()}`, onChambersSaveNack);

                return reject(data.device_id);
            };

            this._joinedChannel?.listen(`.${StationEvents.chambersSaveAck()}`, onChambersSaveAck);
            this._joinedChannel?.listen(`.${StationEvents.chambersSaveNack()}`, onChambersSaveNack);

            this._echo.connector.pusher.send_event(StationEvents.chambersSave(), payload, `presence-${StationEvents.maintenanceStationChannel(stationId)}`);
        });

        return promise;
    }

    public onStationConnected(fn: () => void) {
        if (this._joinedChannel) {
            this._joinedChannel.listen(`.${StationEvents.stationConnected()}`, fn);
        }
    }

    public onStationDisconnected(fn: () => void) {
        if (this._joinedChannel) {
            this._joinedChannel.listen(`.${StationEvents.stationDisconnected()}`, fn);
        }
    }

    private _configureChannelAuthorizer(userAccessToken: string) {
        this._echo.connector.pusher.config.channelAuthorizer = (params, callback) => {
            axios
                .post(
                    this._channelAuthAuthorizerApiUrl,
                    {
                        socket_id: params.socketId,
                        channel_name: params.channelName,
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${userAccessToken}`,
                        },
                    }
                )
                .then((response) => {
                    callback(null, response.data);
                })
                .catch((error) => {
                    if (axios.isAxiosError(error)) {
                        const message = (error as AxiosError<{ message: string }>).response?.data?.message;

                        const callbackError = message ? new Error(message) : error;

                        callback(callbackError, null);
                    } else {
                        callback(error, null);
                    }
                });
        };
    }
}
